import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useBackup } from '@/hooks/backup/useBackup';
import { useToast } from '@/hooks/use-toast';
import { 
  Database, 
  Download, 
  Upload, 
  Shield, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Settings,
  Play,
  Pause,
  RotateCcw,
  HardDrive,
  Calendar,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';

export const BackupDashboard: React.FC = () => {
  const {
    configurations,
    backupJobs,
    restoreJobs,
    verifications,
    backupStats,
    loading,
    runningJobs,
    runningRestores,
    createBackupConfiguration,
    toggleBackupConfiguration,
    createBackup,
    scheduleBackup,
    restoreBackup,
    verifyBackup,
    refreshData
  } = useBackup();
  
  const { toast } = useToast();
  const [createConfigOpen, setCreateConfigOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackupJob, setSelectedBackupJob] = useState<any>(null);

  const handleCreateConfig = async (formData: FormData) => {
    const config = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      backup_type: formData.get('backup_type') as string,
      schedule_cron: formData.get('schedule_cron') as string,
      retention_days: parseInt(formData.get('retention_days') as string),
      storage_config: {
        type: formData.get('storage_type') as string,
        path: formData.get('storage_path') as string
      }
    };

    const success = await createBackupConfiguration(config);
    if (success) {
      toast({
        title: "Success",
        description: "Backup configuration created successfully"
      });
      setCreateConfigOpen(false);
    } else {
      toast({
        title: "Error", 
        description: "Failed to create backup configuration",
        variant: "destructive"
      });
    }
  };

  const handleCreateBackup = async (configId: string) => {
    const success = await createBackup(configId);
    if (success) {
      toast({
        title: "Success",
        description: "Backup started successfully"
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to start backup",
        variant: "destructive"
      });
    }
  };

  const handleRestoreBackup = async (formData: FormData) => {
    const targetEnv = formData.get('target_environment') as string;
    const selectedTables = formData.getAll('selected_tables') as string[];
    
    const success = await restoreBackup(selectedBackupJob.id, targetEnv, selectedTables);
    if (success) {
      toast({
        title: "Success",
        description: "Restore started successfully"
      });
      setRestoreDialogOpen(false);
    } else {
      toast({
        title: "Error",
        description: "Failed to start restore",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, icon: Clock },
      running: { variant: 'default' as const, icon: Loader2 },
      completed: { variant: 'default' as const, icon: CheckCircle },
      failed: { variant: 'destructive' as const, icon: XCircle },
      cancelled: { variant: 'secondary' as const, icon: XCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Backup & Recovery</h1>
          <p className="text-muted-foreground">
            Manage your database backups and restore operations
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refreshData} variant="outline">
            <RotateCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={createConfigOpen} onOpenChange={setCreateConfigOpen}>
            <DialogTrigger asChild>
              <Button>
                <Settings className="h-4 w-4 mr-2" />
                New Configuration
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Backup Configuration</DialogTitle>
                <DialogDescription>
                  Set up a new backup configuration with scheduling and retention policies.
                </DialogDescription>
              </DialogHeader>
              <form action={handleCreateConfig} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input name="name" required />
                  </div>
                  <div>
                    <Label htmlFor="backup_type">Backup Type</Label>
                    <Select name="backup_type" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Full Backup</SelectItem>
                        <SelectItem value="incremental">Incremental</SelectItem>
                        <SelectItem value="differential">Differential</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea name="description" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="schedule_cron">Schedule (Cron)</Label>
                    <Input name="schedule_cron" placeholder="0 2 * * *" />
                  </div>
                  <div>
                    <Label htmlFor="retention_days">Retention Days</Label>
                    <Input name="retention_days" type="number" defaultValue="30" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="storage_type">Storage Type</Label>
                    <Select name="storage_type" defaultValue="local">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="local">Local Storage</SelectItem>
                        <SelectItem value="s3">Amazon S3</SelectItem>
                        <SelectItem value="gcs">Google Cloud Storage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="storage_path">Storage Path</Label>
                    <Input name="storage_path" placeholder="/backups" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCreateConfigOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Create Configuration</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Configurations</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{backupStats.total_configs}</div>
            <p className="text-xs text-muted-foreground">
              {backupStats.active_configs} active
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Backups</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{backupStats.total_backups}</div>
            <p className="text-xs text-muted-foreground">
              {backupStats.successful_backups} successful
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(backupStats.storage_used)}</div>
            <p className="text-xs text-muted-foreground">
              Across all backups
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Backup</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {backupStats.last_backup_time ? format(new Date(backupStats.last_backup_time), 'MMM dd') : 'Never'}
            </div>
            <p className="text-xs text-muted-foreground">
              {backupStats.last_backup_time ? format(new Date(backupStats.last_backup_time), 'HH:mm') : 'No backups yet'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="configurations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="configurations">Configurations</TabsTrigger>
          <TabsTrigger value="backups">Backup Jobs</TabsTrigger>
          <TabsTrigger value="restores">Restore Jobs</TabsTrigger>
          <TabsTrigger value="verifications">Verifications</TabsTrigger>
        </TabsList>

        <TabsContent value="configurations" className="space-y-4">
          {configurations.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Ingen sikkerhetskopi-konfigurasjoner</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Opprett din første sikkerhetskopi-konfigurasjon for å beskytte dataene dine.
              </p>
              <Button onClick={() => setCreateConfigOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Opprett første konfigurasjon
              </Button>
            </div>
          ) : (
          <div className="grid gap-4">
            {configurations.map((config) => (
              <Card key={config.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {config.name}
                        {config.is_active ? (
                          <Badge variant="default">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{config.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCreateBackup(config.id)}
                        disabled={runningJobs.some(job => job.configuration_id === config.id)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Backup Now
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleBackupConfiguration(config.id, !config.is_active)}
                      >
                        {config.is_active ? (
                          <>
                            <Pause className="h-4 w-4 mr-2" />
                            Disable
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <div className="font-medium">{config.backup_type}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Schedule:</span>
                      <div className="font-medium">{config.schedule_cron || 'Manual'}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Retention:</span>
                      <div className="font-medium">{config.retention_days} days</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <div className="font-medium">{format(new Date(config.created_at), 'MMM dd, yyyy')}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="backups" className="space-y-4">
          <div className="space-y-4">
            {backupJobs.map((job) => (
              <Card key={job.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{job.backup_type} Backup</CardTitle>
                      <CardDescription>
                        Created: {format(new Date(job.created_at), 'MMM dd, yyyy HH:mm')}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(job.status)}
                      {job.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedBackupJob(job);
                            setRestoreDialogOpen(true);
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Restore
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">File Size:</span>
                      <div className="font-medium">
                        {job.file_size_bytes ? formatBytes(job.file_size_bytes) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Started:</span>
                      <div className="font-medium">
                        {job.started_at ? format(new Date(job.started_at), 'HH:mm') : 'Not started'}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Completed:</span>
                      <div className="font-medium">
                        {job.completed_at ? format(new Date(job.completed_at), 'HH:mm') : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">File Path:</span>
                      <div className="font-medium text-xs">{job.file_path || 'N/A'}</div>
                    </div>
                  </div>
                  {job.error_message && (
                    <div className="mt-4 p-3 bg-destructive/10 rounded-md">
                      <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        <span className="font-medium">Error:</span>
                      </div>
                      <p className="text-sm mt-1">{job.error_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="restores" className="space-y-4">
          <div className="space-y-4">
            {restoreJobs.map((job) => (
              <Card key={job.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Restore to {job.target_environment}</CardTitle>
                      <CardDescription>
                        Started: {format(new Date(job.created_at), 'MMM dd, yyyy HH:mm')}
                      </CardDescription>
                    </div>
                    {getStatusBadge(job.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Tables:</span>
                      <div className="font-medium">
                        {job.restored_tables?.length || 0} tables
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Started:</span>
                      <div className="font-medium">
                        {job.started_at ? format(new Date(job.started_at), 'HH:mm') : 'Not started'}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Completed:</span>
                      <div className="font-medium">
                        {job.completed_at ? format(new Date(job.completed_at), 'HH:mm') : 'N/A'}
                      </div>
                    </div>
                  </div>
                  {job.error_message && (
                    <div className="mt-4 p-3 bg-destructive/10 rounded-md">
                      <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        <span className="font-medium">Error:</span>
                      </div>
                      <p className="text-sm mt-1">{job.error_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="verifications" className="space-y-4">
          <div className="space-y-4">
            {verifications.map((verification) => (
              <Card key={verification.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{verification.verification_type} Verification</CardTitle>
                      <CardDescription>
                        Created: {format(new Date(verification.created_at), 'MMM dd, yyyy HH:mm')}
                      </CardDescription>
                    </div>
                    {getStatusBadge(verification.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Verified:</span>
                    <div className="font-medium">
                      {verification.verified_at ? format(new Date(verification.verified_at), 'MMM dd, yyyy HH:mm') : 'Not verified'}
                    </div>
                  </div>
                  {verification.error_message && (
                    <div className="mt-4 p-3 bg-destructive/10 rounded-md">
                      <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        <span className="font-medium">Error:</span>
                      </div>
                      <p className="text-sm mt-1">{verification.error_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Backup</DialogTitle>
            <DialogDescription>
              Choose the target environment and tables to restore.
            </DialogDescription>
          </DialogHeader>
          <form action={handleRestoreBackup} className="space-y-4">
            <div>
              <Label htmlFor="target_environment">Target Environment</Label>
              <Select name="target_environment" defaultValue="staging">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="development">Development</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tables to Restore (Select all for full restore)</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {['interviews', 'users', 'profiles', 'comments', 'user_activities'].map((table) => (
                  <div key={table} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="selected_tables"
                      value={table}
                      defaultChecked
                      className="rounded border-gray-300"
                    />
                    <Label className="text-sm">{table}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRestoreDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Start Restore</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};